<template>
  <div class="siren">
    <!-- 測試音效播放失敗所顯示的 Dialog -->
    <el-dialog
      :visible.sync="showSirenTestDialog"
      :title="$t('AudioPermissionGuide.Title')"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
      width="600px"
    >
      <el-tabs v-model="activeTab">
        <el-tab-pane label="Chrome" name="chrome">
          <div class="audio-guide__method">
            <ol>
              <li>
                {{ $t('AudioPermissionGuide.Chrome.Steps[0][0]') }}
                <img
                  src="@/assets/img/alarm/chrome-tune.png"
                  alt="chrome tune"
                  style="width: 24px; height: 24px; vertical-align: middle"
                />
                {{ $t('AudioPermissionGuide.Chrome.Steps[0][1]') }}
              </li>
              <li v-for="(step, index) in $t('AudioPermissionGuide.Chrome.Steps').slice(1)" :key="index + 1">
                {{ step }}
              </li>
            </ol>
          </div>
        </el-tab-pane>
        <el-tab-pane label="Edge" name="edge">
          <div class="audio-guide__method">
            <ol>
              <li v-for="(step, index) in $t('AudioPermissionGuide.Edge.Steps')" :key="index">
                {{ step }}
              </li>
            </ol>
          </div>
        </el-tab-pane>
      </el-tabs>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="onConfirmSirenTest">{{ $t('General.OK') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

export default {
  name: 'Siren',
  data () {
    return {
      intercomEmergencyServiceId: 60, // 對講機緊急通話 id
      showSirenTestDialog: false,
      activeTab: 'chrome',
      isSirenPlaying: false,
      audio: null,
      playingTimerId: null,
      currentSirenLevel: null
    }
  },
  computed: {
    ...mapGetters(['isServiceEnabled']),
    ...mapState({
      communityMainService: state => state.localStorage.communityMainService
    })
  },
  watch: {
    // 功能開關若有變化，則觸發測試音效權限
    communityMainService: {
      handler (newVal, oldVal) {
        // 斷線時 firebase 會在 localStorage 更新 firebase:previous_websocket_failure、接著觸發此監聽
        // 故判斷只在真正有變化時才測試
        const newValStr = JSON.stringify(newVal)
        const oldValStr = JSON.stringify(oldVal)
        if (newValStr !== oldValStr) {
          this.testSirenAvailable()
        }
      },
      deep: true
    }
  },
  mounted () {
    // 測試音效權限
    this.testSirenAvailable()
    // 監聽 service worker 訊息
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('message', this.onServiceWorkerMessage)
    }
  },
  beforeDestroy () {
    // 取消監聽 service worker 訊息
    if (navigator.serviceWorker) {
      navigator.serviceWorker.removeEventListener('message', this.onServiceWorkerMessage)
    }
    // 停止所有音效
    this.stopSiren()
    // 清除計時器
    if (this.playingTimerId) {
      clearTimeout(this.playingTimerId)
      this.playingTimerId = null
    }
  },
  methods: {
    /**
     * 測試警報音效是否可用
     */
    testSirenAvailable () {
      // 如果服務未啟用，則不進行測試
      if (!this.isServiceEnabled(this.intercomEmergencyServiceId)) {
        return
      }
      try {
        // 創建測試用音效
        const testAudio = new Audio(require('@/assets/audio/level3-siren.mp3'))
        testAudio.volume = 0.01 // 最小音量
        // 嘗試播放
        const playPromise = testAudio.play()
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              // 播放成功，立即停止
              testAudio.pause()
              testAudio.currentTime = 0
            })
            .catch((error) => {
              // 播放失敗，顯示 Dialog
              this.showSirenTestDialog = true
              this.$sendGAEvent('handled error', {
                value: '警報音效測試失敗',
                error_name: error.name,
                error_message: error.message
              })
            })
        }
      } catch (error) {
        console.log('警報音效測試失敗:', error)
        this.showSirenTestDialog = true
      }
    },
    /**
     * 警報測試 Dialog - 點擊確認
     */
    onConfirmSirenTest () {
      this.showSirenTestDialog = false
    },
    /**
     * 處理 Service Worker 訊息
     * @param {MessageEvent} event - Service Worker 訊息事件
     */
    onServiceWorkerMessage (event) {
      // event.data.TagLevel 有時會拿到字串，有時會拿到數字
      if (event.data && event.data.FeatureName === 'BA-alert' && event.data.TabletId && [3, 4].includes(parseInt(event.data.TagLevel))) {
        // 為 L3, L4 私區告警
        const tagLevel = parseInt(event.data.TagLevel)
        if (!this.isSirenPlaying || tagLevel > this.currentSirenLevel) {
          // 如果當前未在播放或是警報級別較高，則會播放新的警報
          if (sessionStorage.getItem('isLastFocus') === 'true') {
            // 如果當前分頁是最新 focus 到的分頁，則播放警報
            this.playSiren(tagLevel)
          } else {
            // 如果當前分頁不是最新 focus 到的分頁，則停止播訪警報
            this.stopSiren()
          }
        }
      }
    },
    /**
     * 播放指定級別的警報音效
     * @param {number} level - 警報級別 (3 或 4)
     */
    playSiren (level) {
      // 停止當前播放的警報
      this.stopSiren()
      // 根據級別選擇音效檔案
      const audioPath = level === 3
        ? require('@/assets/audio/level3-siren.mp3')
        : require('@/assets/audio/level4-siren.mp3')
      // 創建音效
      this.audio = new Audio(audioPath)
      // 設定音量為最大值
      this.audio.volume = 1.0
      // 播放音效
      try {
        this.audio.play()
        // 更新狀態
        this.isSirenPlaying = true
        this.currentSirenLevel = level
        // 設定 5 秒後停止播放
        this.playingTimerId = setTimeout(() => {
          this.stopSiren()
        }, 6000)
      } catch (error) {
        console.error('播放警報音效失敗:', error)
        this.stopSiren()
        this.showSirenTestDialog = true
      }
    },
    /**
     * 停止播放警報音效
     */
    stopSiren () {
      if (this.audio) {
        this.audio.pause()
        this.audio.currentTime = 0
        this.audio = null
      }
      if (this.playingTimerId) {
        clearTimeout(this.playingTimerId)
        this.playingTimerId = null
      }
      this.isSirenPlaying = false
      this.currentSirenLevel = null
    }
  }
}
</script>

<style lang="scss" scoped>
.siren {
  ::v-deep .el-dialog {
    @include basicDialog;
    .dialog-footer{
      display: flex;
      justify-content: center;
    }
  }
  .audio-guide__method {
    margin-bottom: 20px;
    ol {
      padding-left: 20px;
      li {
        list-style: decimal;
        line-height: 24px;
      }
    }
  }
}
</style>
